import { useTranslation } from "react-i18next";

import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  GisStudioLoadingSkeleton,
  GisWorkspaceLoadingSkeleton,
} from "@/components/gis/GisLoadingSkeleton";
import { getOperationsArea } from "@/features/operations/navigation";
import { OperationsLoadingSkeleton } from "./OperationsLoadingSkeleton";

const routeLoadingCardKeys = ["route-card-1", "route-card-2", "route-card-3"];

type RouteLoadingFallbackProps = {
  pathname?: string;
};

function resolvePathname(pathname?: string): string {
  if (pathname) return pathname;
  if (typeof window !== "undefined") return window.location.pathname;
  return "/";
}

function shouldShowGisPanel(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(new URLSearchParams(window.location.search).get("panel"));
}

function getOperationsSection(pathname: string): string | undefined {
  const match = pathname.match(
    /^\/projects\/[^/]+\/operations(?:\/([^/]+))?(?:\/([^/]+))?/
  );
  return getOperationsArea(match?.[1]) ? match?.[2] : match?.[1];
}

function AuthLoadingFallback({ label }: { label: string }) {
  return (
    <output
      data-testid="auth-loading-skeleton"
      className="flex min-h-svh items-center justify-center bg-zinc-50 px-4 dark:bg-zinc-950"
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
    >
      <Card aria-hidden="true" className="w-full max-w-md space-y-5 p-6">
        <div className="space-y-2">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-4 w-64 max-w-full" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      </Card>
    </output>
  );
}

function DocumentLoadingFallback({ label }: { label: string }) {
  return (
    <output
      data-testid="document-loading-skeleton"
      className="mx-auto block min-h-[calc(100svh-4rem)] w-full max-w-4xl space-y-5 px-4 py-8 sm:px-6"
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
    >
      <Skeleton className="h-8 w-72 max-w-full" />
      <Card aria-hidden="true" className="space-y-4 p-6">
        <Skeleton className="h-5 w-2/5" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </Card>
    </output>
  );
}

export function RouteLoadingFallback({ pathname }: RouteLoadingFallbackProps) {
  const { t } = useTranslation("common");
  const label = t("loading");
  const currentPathname = resolvePathname(pathname);
  const operationsSection = getOperationsSection(currentPathname);

  if (
    /^\/gis\/[^/]+\/studio(?:\/|$)/.test(currentPathname) ||
    currentPathname.startsWith("/studio/")
  ) {
    return <GisStudioLoadingSkeleton label={label} />;
  }

  if (currentPathname.includes("/operations")) {
    return (
      <OperationsLoadingSkeleton label={label} section={operationsSection} />
    );
  }

  if (currentPathname === "/gis" || currentPathname.startsWith("/gis/")) {
    return (
      <GisWorkspaceLoadingSkeleton
        label={label}
        showLayerPanel={shouldShowGisPanel()}
      />
    );
  }

  if (currentPathname === "/login" || currentPathname.startsWith("/invite/")) {
    return <AuthLoadingFallback label={label} />;
  }

  if (
    currentPathname === "/privacy-policy" ||
    currentPathname === "/terms-and-conditions" ||
    currentPathname.startsWith("/share/") ||
    currentPathname.startsWith("/exports/download/")
  ) {
    return <DocumentLoadingFallback label={label} />;
  }

  return (
    <output
      className="block min-h-[calc(100svh-8rem)] space-y-5 py-4 md:py-6"
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
    >
      <div aria-hidden="true" className="space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-64 max-w-[75vw]" />
          </div>
          <Skeleton className="h-11 w-full sm:w-36" />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {routeLoadingCardKeys.map((key) => (
            <Card key={key} className="space-y-4 p-5">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="ml-auto h-10 w-28" />
            </Card>
          ))}
        </div>
      </div>
    </output>
  );
}
